import React from 'react';
import { Clock, Calendar, RotateCw } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';

interface TimerTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

export const TimerTab: React.FC<TimerTabProps> = ({ data, onChange }) => {
    // Normalization: treat 'duration' as 'cycle' since it was removed
    const timerType = (data.timerType === 'duration' ? 'cycle' : data.timerType) || 'cycle';

    return (
        <div className="p-6 space-y-6">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-900 flex gap-3">
                <Clock className="text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-purple-800 dark:text-purple-300">Timer Configuration</p>
                    <p className="text-[11px] text-purple-700 dark:text-purple-400 leading-relaxed">
                        Configure when this timer event triggers. Supports repeating cycles, cron expressions, or specific dates.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <PropertyField 
                    config={{ 
                        name: 'timerType', 
                        label: 'Timer Type', 
                        type: 'select', 
                        options: [
                            { value: 'cycle', label: 'Cycle (Repeating)' },
                            { value: 'cron', label: 'Cron Expression' },
                            { value: 'date', label: 'Specific Date' }
                        ],
                        defaultValue: 'cycle'
                    }}
                    value={timerType}
                    onChange={onChange}
                />

                {timerType === 'cycle' && (
                    <div className="animate-in fade-in slide-in-from-top-2 duration-200 space-y-4">
                        <PropertyField 
                            config={{ 
                                name: 'timerDefinition', 
                                label: 'Cycle Interval (ISO 8601)', 
                                type: 'text', 
                                placeholder: 'R3/PT10M',
                                description: 'e.g., R/PT1M (Every 1 min), R3/PT10H (3 times, every 10 hours).'
                            }}
                            value={data.timerDefinition}
                            onChange={onChange}
                        />
                         <PropertyField 
                            config={{ 
                                name: 'endDate', 
                                label: 'End Date (Optional)', 
                                type: 'datetime', 
                                description: 'Stop repeating after this date.' 
                            }}
                            value={data.endDate}
                            onChange={onChange}
                        />
                    </div>
                )}

                {timerType === 'cron' && (
                    <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                        <PropertyField 
                            config={{ 
                                name: 'timerDefinition', 
                                label: 'Cron Expression', 
                                type: 'text', 
                                placeholder: '0 0 * * *',
                                description: 'Standard cron syntax (e.g. */5 * * * *)'
                            }}
                            value={data.timerDefinition}
                            onChange={onChange}
                        />
                    </div>
                )}

                {timerType === 'date' && (
                    <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                         <PropertyField 
                            config={{ 
                                name: 'timerDate', 
                                label: 'Trigger Date', 
                                type: 'datetime', 
                                description: 'The specific date and time to fire.' 
                            }}
                            value={data.timerDate}
                            onChange={onChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};